import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import { history } from '@umijs/max';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import RightContent from '@/components/RightContent';
import Footer from '@/components/Footer';
import { currentUser as queryCurrentUser, queryPermission } from './services/tconnect/user';
import { checkIp } from '@/services/tconnect/ip-whitelist';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { Alert } from 'antd';

const loginPath = '/auth/login/';
const blockedIpPath = '/blocked-ip';

async function runIpCheck(): Promise<{ allowed: boolean; ip?: string } | null> {
  try {
    return await checkIp();
  } catch {
    return null;
  }
}

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  fetchAllInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    try {
      const msg = await queryCurrentUser();
      return { ...msg };
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchPermission = async (): Promise<string[]> => {
    try {
      const msg = await queryPermission();
      return msg;
    } catch (error) {}
    return [];
  };

  const fetchAllInfo = async (): Promise<API.CurrentUser | undefined> => {
    const [currentUser, permissions] = await Promise.all([fetchUserInfo(), fetchPermission()]);
    if (currentUser && permissions) {
      currentUser.permissions = permissions;
    }
    return { ...currentUser };
  };

  const currentPath = history.location.pathname;

  const ipCheck = await runIpCheck();

  if (ipCheck && !ipCheck.allowed) {
    sessionStorage.setItem('blocked_ip', ipCheck.ip || '');
    if (currentPath !== blockedIpPath) history.push(blockedIpPath);
    return { fetchUserInfo, fetchAllInfo, settings: defaultSettings };
  }

  if (currentPath === blockedIpPath) {
    history.push('/');
    return { fetchUserInfo, fetchAllInfo, settings: defaultSettings };
  }

  const currentUser = await fetchAllInfo();
  return { fetchUserInfo, currentUser, fetchAllInfo, settings: defaultSettings };
}

let isInitialLoad = true;

// Fires for ALL route changes regardless of layout (covers layout:false routes too)
export async function onRouteChange({ location }: { location: { pathname: string } }) {
  if (isInitialLoad) {
    isInitialLoad = false;
    return; // getInitialState already handles the first load
  }

  const path = location.pathname;

  const ipCheck = await runIpCheck();

  if (ipCheck && !ipCheck.allowed) {
    sessionStorage.setItem('blocked_ip', ipCheck.ip || '');
    if (path !== blockedIpPath) history.push(blockedIpPath);
    return;
  }

  // IP now allowed but stuck on blocked page → go to index
  if (path === blockedIpPath) {
    history.push('/');
  }
}

let waterMark: false | { content: string } = false;
if (APP_NAME !== 'false') {
  waterMark = { content: APP_NAME };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    rightContentRender: () => <RightContent />,
    disableContentMargin: false,
    headerTitleRender: (logo) => <a>{logo}</a>,
    waterMarkProps: waterMark,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      const path = location.pathname;
      if (!initialState?.currentUser && path !== loginPath) {
        history.push(loginPath);
      }
    },
    links: [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {REACT_APP_ENV === 'prodxxx' && (
            <Alert
              message="Đây là môi trường production mọi thao tác hãy cẩn thận!"
              style={{ margin: '40px 40px 0px 40px' }}
              type="error"
            />
          )}
          {children}
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: API_URL,
  ...errorConfig,
};
